import {
  ReactNode,
  useCallback,
  useMemo,
  useState,
  createContext,
} from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  children: ReactNode;
};

type Error = {
  message?: string | ReactNode;
};

export type ErrorContextType = {
  error: Error | undefined;
  showError: (error?: Error) => void;
  removeError: () => void;
};

export const ErrorContext = createContext<ErrorContextType>(
  {} as ErrorContextType,
);

export function ErrorProvider({ children }: Props): React.JSX.Element {
  const { t } = useTranslation('error');
  const [error, setError] = useState<Error | undefined>();

  const showError = useCallback((errorParam: Error = {}) => {
    const defaultError: Error = {
      message: t('generic'),
    };

    setError({
      ...defaultError,
      ...errorParam,
    });
  }, []);

  const removeError = useCallback(() => {
    setError(undefined);
  }, []);

  const errorContextValue = useMemo(
    () => ({
      error,
      showError,
      removeError,
    }),
    [error, showError, removeError],
  );
  return (
    <ErrorContext.Provider value={errorContextValue}>
      {children}
    </ErrorContext.Provider>
  );
}
